import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type GetDistributorGroupByUserEndpointResponsePagedResponse =
  components['schemas']['GetDistributorGroupByUserEndpoint_GetDistributorGroupByUserEndpointPagedResponse'];

export interface UseDistributorGroupByUserReturn extends BaseQuery {
  pagedDistributorGroups: GetDistributorGroupByUserEndpointResponsePagedResponse | null;
}

// Define allowed sort values based on backend validation
export const DISTRIBUTOR_GROUP_SORT_OPTIONS = ['DistributorName', 'Role'] as const;
export type DistributorGroupSortBy = (typeof DISTRIBUTOR_GROUP_SORT_OPTIONS)[number];

export function useDistributorGroupByUser(
  userId: string,
  page: number,
  pageSize: number,
  sortedBy: DistributorGroupSortBy | null,
  enabled: boolean = true,
): UseDistributorGroupByUserReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getDistributorGroupByUser = async (organizationId: number, userId: string) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/users/{userId}/distributor-groups', {
      params: {
        path: { organizationId, userId: userId },
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetDistributorGroupByUserEndpointResponsePagedResponse;
  };

  const isQueryEnabled = enabled && !!userId && organizationId > 0;

  const useDistributorGroupByUserQuery = useQuery({
    queryKey: ['distributorGroupByUser', organizationId, userId, page, pageSize, sortedBy],
    queryFn: () => getDistributorGroupByUser(organizationId, userId),
    enabled: isQueryEnabled,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = useDistributorGroupByUserQuery;

  return {
    pagedDistributorGroups: data ?? null,
    isLoading: isQueryEnabled && (isLoading || isPending),
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
